import * as THREE from 'three';
import { PlayerInventory, ITEMS, type ItemId } from '../game/inventory';
import type { DurabilityStore } from '../game/durability';
import { consumableOf } from '../game/consumables';
import { seedFromEating, forageSeedDrops, forageBonusBerries } from '../game/farming';
import { RECIPES } from '../ui/crafting';
import { TOOLS, TORCH, REPAIR_COST, FARM, NODES } from '../config';
import type { Player } from '../game/player';
import type { DroppedItemField } from '../game/dropped';
import type { ForageField } from '../game/forage';
import type { ChestManager } from '../game/chest';
import type { CampfireManager } from '../game/campfire';
import { deployFoot, type DeployManager, type DeployId } from '../game/deployables';
import { CRAFTABLE_IDS, type CraftableId } from '../game/craftables';
import { LOCAL_PLAYER } from '../game/identity';
import type { NodeField } from '../game/nodes';
import { rollAssetLoot, type AssetField } from '../game/assetField';
import type { CorpseManager } from '../game/corpse';
import type { Builder } from '../game/building';
import type { BuildTier } from '../engine/materials';
import type { ServerInfo, Account } from '../net';
import type { Backend } from './backend';
import type { JoinInfo, InputCommand, ActionIntent, ServerEvent, Snapshot } from './protocol';

// The systems the local authority drives. These are the same objects the renderer already holds; here
// they are the source of truth (spec section 19). In production these live in the Durable Object instead.
export interface LocalDeps {
  player: Player;
  inventory: PlayerInventory;
  dropped: DroppedItemField;
  forage: ForageField;
  chests: ChestManager;
  campfires: CampfireManager;
  deployables: DeployManager;
  nodeField: NodeField;
  assets: AssetField;
  corpses: CorpseManager;
  durability: DurabilityStore;
  builder: Builder;
  spawn: () => THREE.Vector3; // a beach point; player.respawn consumes it directly
}

// The authority grants this loadout on join. Owner-locked: a rock and a torch, NOTHING else -- the
// hammer + blueprint are crafted (20 wood each in the craft menu), so earning your build tools is the
// first two minutes of the game.
const START_ITEMS: Array<{ id: ItemId; n: number }> = [
  { id: 'rock', n: 1 }, { id: 'torch', n: 1 }, { id: 'guidebook', n: 1 },
];

// Crafted doors don't free-place; they snap into a doorway as a swinging leaf (builder.placeCraftedDoor).
// Exported so NetBackend's remote applier deploys another player's door/window exactly the same way.
export const DOOR_IDS = new Set<ItemId>(['wooden_door', 'metal_door', 'metal_double_door', 'reinforced_door']);
// Window deployables snap into a wall's window socket; deploying one marks the socket occupied in the
// builder (mirrors deployRules attach:'window' -- kept local so this file stays craftables-agnostic).
export const WINDOW_ITEM_IDS = new Set<ItemId>(['wood_window', 'metal_window', 'glass_window', 'reinforced_window']);

// LocalBackend: the authority runs in this browser. Every intent is applied to the in-process systems
// and the result is emitted as an event, exactly as the networked authority will. localStorage is the
// persistence store (dev + offline only). No snapshots: there are no remote players locally.
export class LocalBackend implements Backend {
  readonly isLocal = true;
  private readonly eventCbs: Array<(e: ServerEvent) => void> = [];
  private deps: LocalDeps | null = null;

  // Wired once the world systems are built (they do not exist when the backend is constructed).
  setDeps(d: LocalDeps): void { this.deps = d; }

  async connect(_server: ServerInfo, _account: Account): Promise<JoinInfo> {
    const d = this.require();
    const v = d.spawn();
    return { worldSeed: 0, serverTimeMs: performance.now(), spawn: { x: v.x, y: v.y, z: v.z, yaw: 0 }, startItems: START_ITEMS };
  }

  disconnect(): void { /* nothing to tear down locally */ }

  now(): number { return performance.now(); }

  // The local player is simulated directly each frame, so there is nothing to send. NetBackend overrides
  // this to ship the command for prediction + reconciliation.
  sendInput(_cmd: InputCommand): void { /* no-op locally */ }

  action(intent: ActionIntent): void {
    const d = this.deps;
    if (!d) return;
    switch (intent.t) {
      case 'eat': this.eat(d, intent.item); break;
      case 'drink': this.drink(d); break;
      case 'bandage': this.bandage(d); break;
      case 'drop': this.drop(d, intent.item, intent.n, intent.slot, intent.fromBar); break;
      case 'craft': this.craft(d, intent.recipe, intent.free); break;
      case 'craftStart': this.craftStart(d, intent.recipe); break;
      case 'craftFinish': this.craftFinish(d, intent.recipe); break;
      case 'craftCancel': this.craftCancel(d, intent.recipe); break;
      case 'forage': this.forage(d, intent.index); break;
      case 'pickup': this.pickup(d, intent.dropId); break;
      case 'deploy': this.deploy(d, intent.item, intent.x, intent.y, intent.z, intent.yaw, intent.sign, intent.host); break;
      case 'gather': this.gather(d, intent.nodeId, intent.tool, intent.hit, intent.cam); break;
      case 'salvage': this.salvage(d, intent.index, intent.tool, intent.hits); break;
      case 'crate': break; // crate-emptied is a pure MP broadcast (the local crate already despawned in closeContainerIfEmpty)
      case 'chopCorpse': this.chopCorpse(d, intent.corpseId); break;
      case 'placeBuild': {
        if (d.builder.place()) {
          const lp = d.builder.lastPlaced;
          this.emit({ t: 'built', op: 'place', pos: lp ? { x: lp.x, y: lp.y, z: lp.z } : undefined, tier: lp?.tier });
        }
        break;
      }
      case 'demolish': {
        const at = this.rayHitPos(intent);
        if (d.builder.demolish(intent.ox, intent.oy, intent.oz, intent.dx, intent.dy, intent.dz)) this.emit({ t: 'built', op: 'demolish', pos: at });
        break;
      }
      case 'upgrade': this.upgrade(d, intent.tier, intent.ox, intent.oy, intent.oz, intent.dx, intent.dy, intent.dz); break;
      case 'repair': this.repair(d, intent.item); break;
      case 'repairBuild': {
        const at = this.rayHitPos(intent);
        if (d.builder.repairAt(intent.ox, intent.oy, intent.oz, intent.dx, intent.dy, intent.dz)) {
          this.emit({ t: 'feed', label: 'REPAIRED', color: 0x6fd08a });
          this.emit({ t: 'built', op: 'repair', pos: at });
        }
        break;
      }
      case 'claimOwn': this.claimOwn(d, intent.x, intent.z); break;
      case 'claimClear': this.claimClear(d, intent.x, intent.z); break;
      case 'claimAddUser': this.claimAddUser(d, intent.x, intent.z, intent.user); break;
      case 'claimRemoveUser': this.claimRemoveUser(d, intent.x, intent.z, intent.user); break;
      // Sleeping-bag rename / give / pickup. State (+ the pickup refund) lives here so SP and the MP owner's
      // local prediction share one path; NetBackend also relays the matching bedNameAt/bedGiveAt/bedRemoveAt op.
      case 'bedRename': d.deployables.renameBedAt(intent.x, intent.z, intent.name); break;
      case 'chestRename': d.chests.renameAt(intent.x, intent.y, intent.z, intent.name); break;
      case 'bedGive': d.deployables.giveBedAt(intent.x, intent.z, intent.owner); this.emit({ t: 'feed', label: `Gave the bag to ${intent.owner}`, color: 0x9be0a0 }); break;
      case 'bedPickup': { const item = d.deployables.removeBedAt(intent.x, intent.z); if (item) { d.inventory.add(item, 1); this.emit({ t: 'feed', label: `Picked up the ${item === 'bed' ? 'bed' : 'sleeping bag'}`, color: 0x9be0a0 }); } break; }
      // Hammer PICK-UP of any placeable (owner 2026-07-12): the item returns to the hand; a full
      // inventory drops it at your feet instead of eating it. Gates (never-list, must-be-empty) live
      // in pickupAt; claim access was gated on the aim in main before the intent was sent.
      case 'deployPickup': {
        const item = d.deployables.pickupAt(intent.x, intent.y, intent.z)
          ?? d.chests.pickupAt(intent.x, intent.y, intent.z)
          ?? d.campfires.pickupAt(intent.x, intent.y, intent.z);
        if (item) {
          if (d.inventory.add(item as ItemId, 1) < 1) d.dropped.scatter([{ id: item as ItemId, amount: 1 }], intent.x, intent.z);
          this.emit({ t: 'feed', label: 'Picked up', color: 0x9be0a0 });
        }
        break;
      }
      case 'respawn': {
        // Respawn at the chosen bag/bed (if it exists + is off cooldown), else a random beach. Using it cools it down.
        const pt = intent.bagId ? d.deployables.useRespawn(Number(intent.bagId), LOCAL_PLAYER, performance.now() / 1000) : null;
        d.player.respawn(pt ? new THREE.Vector3(pt.x, pt.y, pt.z) : d.spawn());
        // ALWAYS give a fresh spawn the starter kit (rock + torch + guide) -- death empties your carry, so
        // without this you respawn with NOTHING, which is game-breaking. Only add what's missing (idempotent).
        for (const [id, n] of [['rock', 1], ['torch', 1], ['guidebook', 1]] as const) if (d.inventory.count(id) === 0) d.inventory.add(id, n);
        this.emit({ t: 'vitals' });
        break;
      }
    }
  }

  onEvent(cb: (e: ServerEvent) => void): () => void {
    this.eventCbs.push(cb);
    return () => { const i = this.eventCbs.indexOf(cb); if (i >= 0) this.eventCbs.splice(i, 1); };
  }

  // Local play has no remote players, so no snapshots ever arrive. NetBackend drives this.
  onSnapshot(_cb: (s: Snapshot) => void): () => void { return () => {}; }

  // --- intent handlers (mirror the inline logic they replace, then emit the result) ---

  private eat(d: LocalDeps, id: ItemId): void {
    if (d.inventory.count(id) <= 0) return;
    const def = consumableOf(id);
    if (!def) return;
    d.inventory.take(id, 1);
    if (def.bandage) { d.player.bandage(); this.emit({ t: 'feed', label: 'Bandage applied', color: 0xeae3d5, kind: 'bandage' }); this.emit({ t: 'vitals' }); return; }
    const g = d.player.eat(def);
    const parts: string[] = [];
    if (g.hp > 0) parts.push(`+${Math.round(g.hp)} HP`);
    if (g.hunger > 0) parts.push(`+${Math.round(g.hunger)} Food`);
    if (g.thirst > 0) parts.push(`+${Math.round(g.thirst)} Water`);
    if (def.poisonSec) parts.push('Poisoned!');
    if (parts.length) this.emit({ t: 'feed', label: parts.join('   '), color: def.poisonSec ? 0x9cd84e : 0x9be0a0, kind: 'eat' });
    // Eating a raw crop hands its seed back (farming bootstrap: crops you find become crops you grow).
    const seed = seedFromEating(id);
    if (seed) { const n = d.inventory.add(seed, FARM.eatSeeds); if (n > 0) this.emit({ t: 'gather', item: seed, amount: n }); }
    this.emit({ t: 'vitals' });
  }

  private drink(d: LocalDeps): void {
    const gained = d.player.drink();
    this.emit({ t: 'feed', label: gained > 0 ? `+${Math.round(gained)} Water` : 'Not thirsty', color: gained > 0 ? 0x6fc8ee : 0x9bb0c0, kind: gained > 0 ? 'drink' : undefined });
    this.emit({ t: 'vitals' });
  }

  private bandage(d: LocalDeps): void {
    d.player.bandage();
    this.emit({ t: 'feed', label: 'Bandage applied', color: 0xeae3d5, kind: 'bandage' });
    this.emit({ t: 'vitals' });
  }

  private drop(d: LocalDeps, id: ItemId, amount?: number, slot?: number, fromBar?: boolean): void {
    // Pinned to an exact stack (drag/fling): take from that slot -- but only if it still holds that item.
    let n = 0;
    if (slot != null) {
      const s = fromBar ? d.inventory.barStack(slot) : d.inventory.stackAt(slot);
      if (s && s.id === id) n = fromBar ? d.inventory.takeFromBarSlot(slot, amount ?? s.n) : d.inventory.takeFromSlot(slot, amount ?? s.n);
    }
    if (n <= 0) { // aggregate fallback (legacy path / stale slot)
      const have = d.inventory.count(id);
      n = d.inventory.take(id, Math.min(have, amount ?? have));
    }
    if (n <= 0) return;
    const yaw = d.player.object.rotation.y;
    d.dropped.drop(id, n, d.player.position.x, d.player.position.z, yaw);
    this.emit({ t: 'dropSpawned', item: id, n, pos: { x: d.player.position.x, y: d.player.position.y, z: d.player.position.z }, yaw });
  }

  // Instant craft: spend + grant in one step. `free` is the DEV MODE path (no cost, no timer).
  private craft(d: LocalDeps, recipe: ItemId, free?: boolean): void {
    const r = RECIPES.find((x) => x.id === recipe);
    if (!r) return;
    if (!free) {
      if (!r.cost.every((c) => d.inventory.count(c.item) >= c.amount)) return;
      for (const c of r.cost) d.inventory.take(c.item, c.amount);
    }
    this.grantCrafted(d, r.id);
  }

  // Timed craft, step 1: validate + SPEND the cost (the queue's timer runs client-side for now; the
  // grant arrives via craftFinish). TODO(M2+): the authority should own the timer too.
  private craftStart(d: LocalDeps, recipe: ItemId): void {
    const r = RECIPES.find((x) => x.id === recipe);
    if (!r || !r.cost.every((c) => d.inventory.count(c.item) >= c.amount)) return;
    for (const c of r.cost) d.inventory.take(c.item, c.amount);
  }

  // Timed craft, step 2: the timer completed -- grant the item (cost was spent at craftStart).
  private craftFinish(d: LocalDeps, recipe: ItemId): void {
    if (!RECIPES.some((x) => x.id === recipe)) return;
    this.grantCrafted(d, recipe);
  }

  // Un-queue a pending craft: refund its cost (whatever fits; overflow scatters at your feet).
  private craftCancel(d: LocalDeps, recipe: ItemId): void {
    const r = RECIPES.find((x) => x.id === recipe);
    if (!r) return;
    for (const c of r.cost) {
      const back = d.inventory.add(c.item, c.amount);
      if (back < c.amount) d.dropped.scatter([{ id: c.item, amount: c.amount - back }], d.player.position.x, d.player.position.z);
    }
  }

  // Grant a finished craft: hotbar-first routing happens inside PlayerInventory.add. A full inventory
  // drops the item at your feet instead of eating it.
  private grantCrafted(d: LocalDeps, id: ItemId): void {
    const added = d.inventory.add(id, 1);
    if (added < 1) {
      d.dropped.scatter([{ id, amount: 1 }], d.player.position.x, d.player.position.z);
      this.emit({ t: 'feed', label: `Inventory full — ${ITEMS[id].name} dropped at your feet`, color: 0xe0b04a });
    } else {
      this.emit({ t: 'gather', item: id, amount: 1, kind: 'craft' });
    }
    if (d.durability.has(id)) d.durability.reset(id); // a freshly-crafted tool is full (clears any broken state on that id)
  }

  private forage(d: LocalDeps, index: number): void {
    const got = d.forage.collect(index);
    if (!got) return;
    const added = d.inventory.add(got.item, got.amount); // capped by slot capacity
    if (added > 0) this.emit({ t: 'gather', item: got.item, amount: added });
    else this.emit({ t: 'feed', label: 'INVENTORY FULL', color: 0xe06a6a });
    // Bonus rolls (farming entry point): wild corn/pumpkin sometimes carry THEIR OWN seed.
    for (const s of forageSeedDrops(got.item)) {
      const n = d.inventory.add(s.id, s.amount);
      if (n > 0) this.emit({ t: 'gather', item: s.id, amount: n });
    }
    // Rare wild berries (a forage find, not a seed) -- eating them yields berry seeds, bootstrapping the loop
    // without a mushroom pick spitting out seeds. Only from mushroom/hemp/apple picks (forest-floor plants).
    if ((got.item === 'mushroom' || got.item === 'apple') && forageBonusBerries() > 0) {
      const n = d.inventory.add('berries', 1);
      if (n > 0) this.emit({ t: 'gather', item: 'berries', amount: n });
    }
  }

  private pickup(d: LocalDeps, dropId: number): void {
    // FULL-INVENTORY GATE: no room at all -> the drop stays on the ground untouched, you get warned.
    const peek = d.dropped.peekById(dropId);
    if (!peek) return;
    if (d.inventory.room(peek.id) <= 0) { this.emit({ t: 'feed', label: 'INVENTORY FULL', color: 0xe06a6a }); return; }
    const got = d.dropped.pickupById(dropId);
    if (!got) return;
    const added = d.inventory.add(got.id, got.amount);
    if (added < got.amount) { // partial fit: re-drop the overflow where it was (no loss)
      d.dropped.scatter([{ id: got.id, amount: got.amount - added }], d.player.position.x, d.player.position.z);
      this.emit({ t: 'feed', label: 'INVENTORY FULL', color: 0xe06a6a });
    }
    if (added <= 0) return;
    if (d.durability.has(got.id)) d.durability.reset(got.id); // a picked-up tool is treated as fresh
    this.emit({ t: 'gather', item: got.id, amount: added });
  }

  private deploy(d: LocalDeps, item: ItemId, x: number, y: number, z: number, yaw: number, sign?: number, host?: string): void {
    if (d.inventory.count(item) <= 0) return;
    // AUTHORITY RE-CHECK (spec section 19): the client ghost already gates on claim access, but the
    // backend must not trust it -- placing anything inside a claim you cannot access is rejected here
    // too, so the MP server port inherits the rule.
    const claim = d.deployables.claimAt(x, z);
    if (claim && !d.deployables.claimHasAccess(claim, LOCAL_PLAYER)) return;
    // SIZE-CLASS re-check: a TALL deployable (hy > 0.6 -- locker/fridge/baseclaim class) must fit
    // under the room's lid. The client ghost already hinted 'Too tall for this room', but the
    // authority must not trust it. Doors are socketed pieces with their own gates.
    if (!DOOR_IDS.has(item)) {
      const hy = deployFoot(item as DeployId)[1];
      if (hy > 0.6 && hy * 2 + 0.25 > d.builder.clearanceAt(x, z, y)) return;
    }
    const pos = new THREE.Vector3(x, y, z);
    // A door snaps into a doorway as a swinging leaf; the builder rejects an occupied socket, and a
    // rejected placement must not consume the item.
    if (DOOR_IDS.has(item)) { if (!d.builder.placeCraftedDoor(x, y, z, yaw, item as 'wooden_door' | 'metal_door' | 'metal_double_door' | 'reinforced_door', sign ?? 1)) return; }
    else if (CRAFTABLE_IDS.has(item as CraftableId)) {
      d.deployables.deploy(item as CraftableId, x, y, z, yaw, host);
      // A window fills its wall socket for good (until the hammer pulls it): mark the socket.
      if (WINDOW_ITEM_IDS.has(item)) d.builder.markWindowOccupied(x, y, z);
    }
    else if (item === 'campfire') d.campfires.deploy(pos, yaw);
    else d.chests.deploy(item === 'bigchest' ? 'big' : 'small', pos, yaw);
    d.inventory.take(item, 1);
  }

  private gather(d: LocalDeps, nodeId: number, tool: ItemId, hit: { x: number; y: number; z: number } | null, cam: { x: number; y: number; z: number }): void {
    const node = d.nodeField.nodeById(nodeId);
    if (!node) return;
    const isTree = node.spotKind === 'tree';
    const tf = TOOLS[tool]; // axe favors wood, pickaxe favors ore, by tier (config); rock = 1x
    const mult = tf ? (isTree ? tf.tree : tf.ore) : 1; // tool sets extraction SPEED (units/swing); the node's TOTAL is fixed
    const hp = hit ? new THREE.Vector3(hit.x, hit.y, hit.z) : null;
    const h = d.nodeField.harvest(node, hp, new THREE.Vector3(cam.x, cam.y, cam.z), mult);
    const added = d.inventory.add(h.item, h.amount); // capped by slot capacity
    this.emit({ t: 'gather', item: h.item, amount: added, kind: isTree ? 'tree' : 'ore', depleted: h.depleted });
    if (added <= 0) this.emit({ t: 'feed', label: 'INVENTORY FULL', color: 0xe06a6a });
    // Metal nodes also drop stone: grant it and feed it as its own line (the PRIMARY carries the depleted/fall cue).
    if (h.secondary) {
      const addedSec = d.inventory.add(h.secondary.item, h.secondary.amount);
      if (addedSec > 0) this.emit({ t: 'gather', item: h.secondary.item, amount: addedSec, kind: 'ore' });
    }
    // Trees also shed a little CLOTH as you chop (owner: incentivize cloth). Probabilistic, keyed to the wood
    // actually granted this swing and tuned to ~5 cloth / 1000 wood: grant the whole part, then roll the
    // fractional remainder for one more, so small per-swing yields still average to the target rate. Cloth is
    // granted locally by the acting client only (remote gatherAt grants no inventory), so no MP divergence.
    if (isTree && added > 0) {
      const expected = added * NODES.treeClothPerWood;
      const cloth = Math.floor(expected) + (Math.random() < expected - Math.floor(expected) ? 1 : 0);
      if (cloth > 0) { const addedCloth = d.inventory.add('cloth', cloth); if (addedCloth > 0) this.emit({ t: 'gather', item: 'cloth', amount: addedCloth, kind: 'tree' }); }
    }
    // Felled an apple tree: its crop shakes loose and scatters on the ground at the stump, one pickup per apple, so
    // the WHOLE group can grab them (not just the chopper). Broadcasts via dropped.onSpawn -> same apples on every client.
    if (isTree && h.apples && h.apples > 0) {
      const batch = Array.from({ length: h.apples }, () => ({ id: 'apple' as ItemId, amount: 1 }));
      d.dropped.scatter(batch, h.pos.x, h.pos.z);
      this.emit({ t: 'feed', label: `The apple tree drops ${h.apples} apples`, color: 0xd06a3a });
    }
    if (d.durability.use(tool)) this.breakTool(tool); // a landed harvest swing wears the tool
  }

  // A tool wore out. It is NOT consumed -- it sits broken (empty bar + X in the hotbar; the durability.broken()
  // gate stops it being used) until the player crafts/picks up a fresh one (which resets the id). Just a feed line.
  private breakTool(tool: ItemId): void {
    this.emit({ t: 'feed', label: `${ITEMS[tool].name} is broken — craft a new one`, color: 0xe06a6a });
  }

  // Smash a destructible prop (barrel / junk pile): 'destroy' assets take several tool swings (the field tracks
  // the hit count). On the break, roll the loot table and SCATTER it on the ground around the prop. (Crates use
  // the open-container flow in main.ts, not this intent.)
  private salvage(d: LocalDeps, index: number, tool?: ItemId, hitsIn?: number): void {
    const pos = d.assets.posAt(index); // read before salvage() re-streams the dead slot
    const hits = hitsIn ?? (tool === 'torch' ? TORCH.smashHits : 1); // torch = slow smasher; tools 1; bullets pass 2 (owner 2026-07-12)
    const res = d.assets.salvage(index, hits);
    if (!res) return;
    // A gather tool wears on each landed smash (the torch drains client-side per swing; a bullet has no durability).
    if (tool && tool !== 'torch' && d.durability.use(tool)) this.breakTool(tool); // gather-tool wear; torch wears client-side, bullets pass no tool
    if (!res.depleted) return; // not broken yet -> the client played the hit thunk; nothing to drop
    if (pos) d.dropped.scatter(rollAssetLoot(res.id), pos.x, pos.z);
    // amount 0 = SFX only, no toast (loot is on the ground); pos+assetId drive the client shatter burst at the prop.
    this.emit({ t: 'gather', item: 'salvage', amount: 0, kind: 'salvage', depleted: true, pos: pos ? { x: pos.x, y: 0, z: pos.z } : undefined, assetId: res.id });
  }

  private chopCorpse(d: LocalDeps, corpseId: number): void {
    const y = d.corpses.chopById(corpseId);
    if (!y) return;
    d.dropped.scatter([{ id: 'bone', amount: y.bone }, { id: 'fat', amount: y.fat }], y.pos.x, y.pos.z); // strewn on the ground, not straight to inventory
  }

  // Upgrade the aimed piece (spends from inventory inside the builder). No grass change (it's a re-skin
  // in place, not a new footprint), but still 'built' so the client plays the tier-tinted knock SFX.
  private upgrade(d: LocalDeps, tier: BuildTier, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): void {
    if (d.builder.upgradeAt(ox, oy, oz, dx, dy, dz, tier)) this.emit({ t: 'built', op: 'upgrade', pos: this.rayHitPos({ ox, oy, oz, dx, dy, dz }), tier });
  }

  // Approximate world position of a hammer-ray hit for SFX (pan/distance): the ray origin projected a
  // short reach forward. Close enough for audio -- these are all short-range aimed swings.
  private rayHitPos(r: { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number }): { x: number; y: number; z: number } {
    return { x: r.ox + r.dx * 2.5, y: r.oy + r.dy * 2.5, z: r.oz + r.dz * 2.5 };
  }

  // Repair the held tool at a repair bench: charge a fixed cost, refill toward the (diminishing) ceiling. Each
  // repair lowers the max 10%, so a tool can be repaired ~9 times. Broken (HP 0) tools that still exist qualify.
  private repair(d: LocalDeps, item: ItemId): void {
    if (!d.durability.has(item)) return;
    const cost = REPAIR_COST[item];
    if (!cost) { this.emit({ t: 'feed', label: `${ITEMS[item].name} cannot be repaired`, color: 0xe06a6a }); return; }
    if (d.durability.current(item) >= d.durability.max(item)) { this.emit({ t: 'feed', label: `${ITEMS[item].name} is at full durability`, color: 0x9bb0c0 }); return; }
    const costItem = cost.item as ItemId;
    if (d.inventory.count(costItem) < cost.amount) { this.emit({ t: 'feed', label: `Need ${cost.amount} ${ITEMS[costItem].name} to repair`, color: 0xe06a6a }); return; }
    const newHp = d.durability.repair(item);
    if (newHp == null) { this.emit({ t: 'feed', label: `${ITEMS[item].name} is worn out — craft a new one`, color: 0xe06a6a }); return; }
    d.inventory.take(costItem, cost.amount);
    const pct = Math.round((newHp / d.durability.max(item)) * 100);
    this.emit({ t: 'feed', label: `${ITEMS[item].name} repaired (max now ${pct}%)`, color: 0x9be0a0 });
  }

  // Base-claim ownership (the #7 hold wheel). The authority resolves the claim by position, validates access, mutates
  // it, and reports the result as a feed line. `who` is always the local player here; NetBackend will use the sender.
  private claimOwn(d: LocalDeps, x: number, z: number): void {
    const cl = d.deployables.claimByPos(x, z); if (!cl) return;
    if (d.deployables.claimTakeOwnership(cl, LOCAL_PLAYER)) this.emit({ t: 'feed', label: 'Base claim is now yours', color: 0x9be0a0 });
    else this.emit({ t: 'feed', label: cl.lock ? 'Locked — destroy it to take over' : 'Already owned', color: 0xe0b04a });
  }
  private claimClear(d: LocalDeps, x: number, z: number): void {
    const cl = d.deployables.claimByPos(x, z); if (!cl) return;
    if (d.deployables.claimClearOwnership(cl, LOCAL_PLAYER)) this.emit({ t: 'feed', label: 'Ownership cleared — base is unclaimed', color: 0xeae3d5 });
    else this.emit({ t: 'feed', label: 'Not the owner', color: 0xe06a6a });
  }
  private claimAddUser(d: LocalDeps, x: number, z: number, user: string): void {
    const cl = d.deployables.claimByPos(x, z); if (!cl) return;
    if (d.deployables.claimAddUser(cl, LOCAL_PLAYER, user)) this.emit({ t: 'feed', label: `${user.trim()} can now use this base`, color: 0x9be0a0 });
    else this.emit({ t: 'feed', label: 'Could not add that user', color: 0xe06a6a });
  }
  private claimRemoveUser(d: LocalDeps, x: number, z: number, user: string): void {
    const cl = d.deployables.claimByPos(x, z); if (!cl) return;
    if (d.deployables.claimRemoveUser(cl, LOCAL_PLAYER, user)) this.emit({ t: 'feed', label: `${user} removed`, color: 0xeae3d5 });
  }

  private emit(e: ServerEvent): void { for (const cb of this.eventCbs) cb(e); }

  private require(): LocalDeps {
    if (!this.deps) throw new Error('LocalBackend used before setDeps');
    return this.deps;
  }
}
